import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import SidebarLayout from './components/SidebarLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import InscripcionesPage from './pages/InscripcionesPage';
import AlumnosPage from './pages/AlumnosPage';
import AsistenciaPage from './pages/AsistenciaPage';
import ExpedientePage from './pages/ExpedientePage';
import BitacoraPage from './pages/BitacoraPage';
import PatrePage from './pages/PatrePage';
import AlertasPage from './pages/AlertasPage';
import ReportesPage from './pages/ReportesPage';
import UsuariosPage from './pages/UsuariosPage';
import AuditoriaPage from './pages/AuditoriaPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import CredencialesPage from './pages/CredencialesPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Para rol "administrativo", checa que el módulo esté dentro de modulos_permitidos.
const ProtectedRoute = ({ children, allowedRoles, moduloAdministrativo }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si es administrativo, valida el módulo permitido individualmente
  if (user.rol === 'administrativo') {
    if (!moduloAdministrativo) return <Navigate to="/dashboard" replace />;
    const modulos = user.modulos_permitidos || [];
    if (!modulos.includes(moduloAdministrativo)) return <Navigate to="/dashboard" replace />;
    return <SidebarLayout><ErrorBoundary>{children}</ErrorBoundary></SidebarLayout>;
  }

  // Para los demás roles, validación clásica
  if (allowedRoles && !allowedRoles.includes(user.rol)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SidebarLayout><ErrorBoundary>{children}</ErrorBoundary></SidebarLayout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute moduloAdministrativo="dashboard">
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/inscripciones" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion']} moduloAdministrativo="inscripciones">
              <InscripcionesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/alumnos" element={
            <ProtectedRoute moduloAdministrativo="alumnos">
              <AlumnosPage />
            </ProtectedRoute>
          } />
          
          <Route path="/asistencia" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion', 'prefectura']} moduloAdministrativo="asistencia">
              <AsistenciaPage />
            </ProtectedRoute>
          } />
          
          <Route path="/expediente" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion', 'prefectura']} moduloAdministrativo="expediente">
              <ExpedientePage />
            </ProtectedRoute>
          } />
          
          <Route path="/bitacora" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion', 'prefectura']} moduloAdministrativo="bitacora">
              <BitacoraPage />
            </ProtectedRoute>
          } />
          
          <Route path="/patre" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion', 'docente']} moduloAdministrativo="patre">
              <PatrePage />
            </ProtectedRoute>
          } />
          
          <Route path="/alertas" element={
            <ProtectedRoute moduloAdministrativo="alertas">
              <AlertasPage />
            </ProtectedRoute>
          } />
          
          <Route path="/reportes" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion', 'prefectura']} moduloAdministrativo="reportes">
              <ReportesPage />
            </ProtectedRoute>
          } />

          <Route path="/credenciales" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion']} moduloAdministrativo="credencialización">
              <CredencialesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/usuarios" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion']}>
              <UsuariosPage />
            </ProtectedRoute>
          } />
          
          <Route path="/auditoria" element={
            <ProtectedRoute allowedRoles={['administrador', 'direccion']}>
              <AuditoriaPage />
            </ProtectedRoute>
          } />
          
          <Route path="/configuracion" element={
            <ProtectedRoute allowedRoles={['administrador']}>
              <ConfiguracionPage />
            </ProtectedRoute>
          } />
          
          {/* Redirect root to dashboard or login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Catch all - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
